// CameraDirector contract (§24.2, §12.7). WP7 owns the implementation: the shot is chosen from the
// presented FrameView each frame — menu diorama, intro sweep, gameplay framing (Sudden Death
// tightens 10%), spectator follow, the final-KO slow-motion close-up (85% framing on the victim),
// the GAME! slow orbit, the Instant Replay / Play of the Match low orbit, and BONK CAM (a low side
// angle 2.5 m from the victim with the killer in frame). Every shot is a look target plus a spherical
// offset (yaw, pitch, distance) on critically damped springs, so cuts between shots are smooth;
// changing frame source, map or round snaps instead. Trauma shake and FOV punches ride on top; Reduced
// Motion removes shake, punches, slow-motion orbits and the intro sweep.
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';
import { NO_SLOT, TUNE } from '@shared/constants';
import { MapId, RoomPhase } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import type { FrameView } from '../../game/FrameView';
import {
  createSafeArea, DEFAULT_HUD, frameBeans, FRAMING, outsideBounds, refineFit, safeAreaFor, VIEW, type FrameWant, type HoldPoint, type HudInsets,
} from './framing';
import { Shake } from './Shake';

export interface CameraDirector {
  readonly camera: PerspectiveCamera;
  update(view: FrameView): void;       // modes chosen from view (intro/play/replay/bonkcam/podium/locker/menu)
  addTrauma(a: number): void;
  punch(amount: number, ms: number): void;
  resize(w: number, h: number, portrait: boolean): void;
}

export interface CameraOptions {
  /** Reduced Motion (§14.4): no shake, punches, slow-motion sweeps or intro sweep. */
  reducedMotion(): boolean;
  /** Screen-shake setting, 0..1. */
  shakeScale(): number;
  /** `?skipIntro=1` also skips the intro camera sweep (§28). */
  skipIntro: boolean;
}

/** A presentation moment the camera frames (set by the Renderer from VFX moments). */
export interface CameraMoment {
  kind: 'finalKo' | 'game';
  /** Sim position of the KO splash. */
  x: number; y: number;
  victim: number;
  winner: number;
}

/** WP7's director: the contract plus what the renderer and post chain read. */
export interface GameCamera extends CameraDirector {
  /** The look target (three.js world) — the tilt-shift focus band follows its screen Y. */
  readonly target: Vector3;
  readonly trauma: number;
  readonly shot: Shot;
  setMoment(m: CameraMoment | null): void;
  /** Forces the next frame to snap (e.g. replay start). */
  cut(): void;
  /** Keeps a sim point in the gameplay frame for `sec` seconds (a KO splash, §11.2). */
  holdInFrame(x: number, y: number, z: number, sec: number): void;
  /**
   * The HUD's footprint (viewport fractions); null = the shipped HUD's defaults for the orientation.
   * Gameplay framing keeps beans clear of it and centers the action in the band it leaves free.
   */
  setHudInsets(h: HudInsets | null): void;
  /** The frame's current vertical offset (NDC units the look target sits above the center). */
  readonly frameShift: number;
}

export type Shot = 'menu' | 'intro' | 'play' | 'follow' | 'finalKo' | 'game' | 'orbit' | 'bonkcam' | 'rewind';

/**
 * §12.7: target ω 4, distance ω 3. Widening to keep a bean in frame uses `distOutOmega` (a flung or
 * falling bean otherwise leaves the frame before a ω 3 spring catches up); tightening keeps ω 3.
 */
const SPRING = { targetOmega: 4, distOmega: 3, distOutOmega: 7, shotOmega: 5, maxStep: 1 / 120 } as const;
const INTRO = { durationTicks: 72, dist: 40, pitchDeg: 70, yaw: -0.5, minSec: 0.4 } as const;
const MIN_HEIGHT = 4;
const LOOK_HEIGHT = 0.5;
/** Replay shots never dip below the void plane (§12.7). */
const VOID_CLEARANCE = 1.2;
const ORBIT = { radius: 6, height: 2.2, degPerSec: 25, fovDeg: 46 } as const;
/**
 * BONK CAM. §11 asks for 2.5 m from the victim; at a 50° FOV that crops a 1.1 m bean to its belly,
 * so the shot keeps 2.5 m as the closest the look point gets and frames from at least 4.2 m.
 */
const BONK = { dist: 4.2, height: 2.0, fovDeg: 48, killerLead: 0.3, maxKillerDist: 7, margin: 1.1, lookHeight: 0.55, floorAbove: 0.9, omega: 10 } as const;
const FINAL = { framing: 0.85, lookDrop: 1.2, omega: 6 } as const;
const GAME = { dist: 8.5, pitchDeg: 30, degPerSec: 18, omega: 3.5 } as const;
/** Punch envelope (§12.7 "FOV × 0.94 for 0.2 s"): a snap in, a hold at full strength, an ease out. */
const PUNCH_ATTACK = 0.18;
const PUNCH_RELEASE = 0.65;
const MENU_SHIFT_X = 3.2;
/**
 * Portrait menu diorama: the menu buttons cover the bottom half and the logo the top, so instead of
 * sliding sideways the Lobby lid is pulled back to fit the narrow width and lifted into the band
 * between them (NDC units the target sits above the center).
 */
const MENU_PORTRAIT = { dist: 34, shift: 0.3 } as const;
/** How far the gameplay target leans toward a held point (KO splash). */
const HOLD_LEAN = 0.25;
const SOURCE_INDEX: Readonly<Record<FrameView['source'], number>> = { online: 0, local: 1, replay: 2 };

/** Critically damped spring state for one scalar. */
class Spring {
  v = 0;
  constructor(public x: number) {}
  step(target: number, omega: number, dt: number): void {
    let left = dt;
    while (left > 0) {
      const h = Math.min(left, SPRING.maxStep);
      this.v += (omega * omega * (target - this.x) - 2 * omega * this.v) * h;
      this.x += this.v * h;
      left -= h;
    }
  }
  snap(x: number): void {
    this.x = x;
    this.v = 0;
  }
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/** The shot's wanted values: look target (sim x, y and height) and spherical offset. */
interface Want { x: number; y: number; h: number; yaw: number; pitch: number; dist: number; fov: number; omega: number }

export function createCameraDirector(opts: CameraOptions): GameCamera {
  const camera = new PerspectiveCamera(VIEW.landscape.fovDeg, 16 / 9, 0.5, 600);
  const tx = new Spring(0);
  const ty = new Spring(0);
  const th = new Spring(LOOK_HEIGHT);
  const yaw = new Spring(0);
  const pitch = new Spring(VIEW.landscape.pitchDeg * MathUtils.DEG2RAD);
  const dist = new Spring(FRAMING.minDist);
  const fov = new Spring(VIEW.landscape.fovDeg);
  const target = new Vector3();
  const frameWant: FrameWant = { x: 0, y: 0, dist: FRAMING.minDist, radius: FRAMING.minR, count: 0 };
  const want: Want = { x: 0, y: 0, h: LOOK_HEIGHT, yaw: 0, pitch: 0, dist: FRAMING.minDist, fov: VIEW.landscape.fovDeg, omega: SPRING.targetOmega };
  const shake = new Shake();
  const right = new Vector3();
  const up = new Vector3();
  let aspect = 16 / 9;
  let portrait = false;
  let shotKey = -1;
  let forceCut = false;
  let shot: Shot = 'play';
  let moment: CameraMoment | null = null;
  let punchAmount = 0;
  let punchLeft = 0;
  let punchTotal = 1;
  let orbitYaw = 0;
  let lastNow = -1;
  let clock = 0;
  let introRound = -1;
  let introDur = 0;
  let introAge = 0;
  const holds: HoldPoint[] = [];
  const liveHolds: HoldPoint[] = [];
  let hudOverride: HudInsets | null = null;
  const safe = createSafeArea();
  /** Wanted and current frame offset (NDC); replays and the GAME! orbit run without the HUD. */
  let shiftWant = 0;
  let shiftXWant = 0;
  const shiftSpring = new Spring(0);
  const shiftXSpring = new Spring(0);
  let viewW = 1;
  let viewH = 1;
  let appliedShiftPx = Number.NaN;
  let appliedShiftXPx = Number.NaN;
  let podium = false;

  function updateSafe(): void {
    const d = podium ? (portrait ? DEFAULT_HUD.podiumPortrait : DEFAULT_HUD.podiumLandscape) : portrait ? DEFAULT_HUD.portrait : DEFAULT_HUD.landscape;
    safeAreaFor(hudOverride ?? d, safe);
  }
  updateSafe();

  /** Applies the frame offset as a view offset (content moves up by `shift`, left by `shiftX` NDC). */
  function applyShift(shift: number, shiftX: number): void {
    const py = Math.round(shift * 0.5 * viewH * 4) / 4;
    const px = Math.round(shiftX * 0.5 * viewW * 4) / 4;
    if (py === appliedShiftPx && px === appliedShiftXPx) return;
    appliedShiftPx = py;
    appliedShiftXPx = px;
    if (Math.abs(py) < 0.25 && Math.abs(px) < 0.25) camera.clearViewOffset();
    else camera.setViewOffset(viewW, viewH, px, py, viewW, viewH);
  }

  function mode(): { fovDeg: number; pitchDeg: number } {
    return portrait ? VIEW.portrait : VIEW.landscape;
  }

  function gameplay(view: FrameView): void {
    const m = mode();
    liveHolds.length = 0;
    const map = MAPS[view.mapId];
    for (const h of holds) if (h.until > clock && outsideBounds(map, h.x, h.y) <= FRAMING.holdLeash) liveHolds.push(h);
    const fw = frameBeans(view, map, aspect, portrait, frameWant);
    if (liveHolds.length > 0) {
      // Lean the target toward held points so the splash sits inside the frame, not on its edge.
      for (const h of liveHolds) {
        fw.x += (h.x - fw.x) * HOLD_LEAN;
        fw.y += (h.y - fw.y) * HOLD_LEAN;
      }
    }
    refineFit(view, fw, aspect, portrait, liveHolds, safe, map);
    want.x = frameWant.x;
    want.y = frameWant.y;
    want.h = LOOK_HEIGHT;
    want.yaw = 0;
    want.pitch = m.pitchDeg * MathUtils.DEG2RAD;
    want.dist = frameWant.dist;
    want.fov = m.fovDeg;
    want.omega = SPRING.targetOmega;
    shot = 'play';
    if (view.source === 'local' && view.mapId === MapId.LOBBY && view.roomPhase === RoomPhase.LOBBY) {
      if (portrait) {
        const c = MAPS[view.mapId].center;
        want.x = c.x;
        want.y = c.y;
        want.dist = Math.max(want.dist, MENU_PORTRAIT.dist);
      } else {
        want.x += MENU_SHIFT_X;
      }
      shot = 'menu';
    }
  }

  function follow(view: FrameView, slot: number): boolean {
    const b = view.beans[slot];
    if (!b || !b.active) return false;
    const m = mode();
    want.x = b.x;
    want.y = b.y;
    want.h = LOOK_HEIGHT;
    want.yaw = 0;
    want.pitch = m.pitchDeg * MathUtils.DEG2RAD;
    want.dist = FRAMING.followDist;
    want.fov = m.fovDeg;
    want.omega = SPRING.targetOmega;
    shot = 'follow';
    return true;
  }

  /** Instant Replay / Play of the Match: a low orbit (r 6, h 2.2, 25°/s) around the focus bean. */
  function orbit(view: FrameView, slot: number, dt: number, rewinding: boolean): void {
    const b = slot !== NO_SLOT ? view.beans[slot] : undefined;
    const map = MAPS[view.mapId];
    const live = b !== undefined && b.active;
    if (!opts.reducedMotion() && !rewinding) orbitYaw += ORBIT.degPerSec * MathUtils.DEG2RAD * dt;
    want.x = live ? b.x : map.center.x;
    want.y = live ? b.y : map.center.y;
    want.h = (live ? Math.max(b.z, TUNE.edge.killZ + VOID_CLEARANCE) : 0) + 0.6;
    want.yaw = orbitYaw;
    want.pitch = Math.atan2(ORBIT.height, ORBIT.radius) + 0.12;
    want.dist = Math.hypot(ORBIT.radius, ORBIT.height);
    want.fov = ORBIT.fovDeg;
    want.omega = SPRING.shotOmega;
    shot = rewinding ? 'rewind' : 'orbit';
  }

  /**
   * BONK CAM: a low side angle at least 2.5 m from the victim, looking at it with the killer in frame.
   * The camera sits on the side of the victim–killer line facing the usual view (+y) and backs off
   * just enough for both beans to fit the horizontal field of view; the look point leans 30% toward
   * the killer. While the victim drops off the rim the camera stays above the floor and tilts down
   * to follow the fall.
   */
  function bonkCam(view: FrameView, victim: number, killer: number, rewinding: boolean): void {
    const v = victim !== NO_SLOT ? view.beans[victim] : undefined;
    if (!v || !v.active) {
      orbit(view, victim, 0, rewinding);
      return;
    }
    const k = killer !== NO_SLOT && killer !== victim ? view.beans[killer] : undefined;
    const hasKiller = k !== undefined && k.active && !k.ghost && Math.hypot(k.x - v.x, k.y - v.y) < BONK.maxKillerDist;
    let kx = 0;
    let ky = -1;
    let kd = 0;
    if (hasKiller) {
      const dx = k.x - v.x;
      const dy = k.y - v.y;
      kd = Math.hypot(dx, dy);
      if (kd > 0.05) {
        kx = dx / kd;
        ky = dy / kd;
      }
    }
    const lead = kd * BONK.killerLead;
    // A flung victim moves fast: lead the look point by the spring's steady-state lag (2/ω of real
    // time at the replay rate), so the victim stays in frame instead of flying out of it.
    const lag = (2 / BONK.omega) * Math.max(0.1, view.timeScale);
    want.x = v.x + kx * lead + v.vx * lag;
    want.y = v.y + ky * lead + v.vy * lag;
    want.h = Math.max(v.z + v.vz * lag, TUNE.edge.killZ + VOID_CLEARANCE) + BONK.lookHeight;
    // Perpendicular to the victim–killer line, on the side nearest the gameplay camera (+y).
    let px = -ky;
    let py = kx;
    if (py < 0) {
      px = -px;
      py = -py;
    }
    want.yaw = Math.atan2(px, py);
    const halfH = Math.tan((BONK.fovDeg * MathUtils.DEG2RAD) / 2);
    const halfW = halfH * aspect;
    // Half-width to fit: the farther bean from the look point plus a body's margin.
    const span = Math.max(lead, kd - lead) + BONK.margin;
    const fit = span / Math.max(0.2, halfW);
    const d = Math.max(BONK.dist, fit);
    want.pitch = Math.atan2(BONK.height, d);
    want.dist = Math.hypot(d, BONK.height);
    want.fov = BONK.fovDeg;
    want.omega = BONK.omega;
    shot = rewinding ? 'rewind' : 'bonkcam';
  }

  /** Final KO: frame the splash at 85% of the gameplay distance, looking a little lower. */
  function finalKo(view: FrameView, m: CameraMoment): void {
    gameplay(view);
    want.x = MathUtils.lerp(want.x, m.x, 0.75);
    want.y = MathUtils.lerp(want.y, m.y, 0.75);
    want.h = LOOK_HEIGHT - FINAL.lookDrop;
    want.dist *= FINAL.framing;
    want.omega = FINAL.omega;
    shot = 'finalKo';
  }

  /** GAME!: a slow orbit around the winner (or the splash when the winner is unknown). */
  function gameOrbit(view: FrameView, m: CameraMoment, dt: number): void {
    const w = m.winner !== NO_SLOT ? view.beans[m.winner] : undefined;
    const alive = w !== undefined && w.active && !w.out;
    const cx = alive ? w.x : m.x;
    const cy = alive ? w.y : m.y;
    if (opts.reducedMotion()) {
      gameplay(view);
      want.x = MathUtils.lerp(want.x, cx, 0.6);
      want.y = MathUtils.lerp(want.y, cy, 0.6);
      want.dist *= FINAL.framing;
      shot = 'game';
      return;
    }
    orbitYaw += GAME.degPerSec * MathUtils.DEG2RAD * dt;
    want.x = cx;
    want.y = cy;
    want.h = 0.7;
    want.yaw = orbitYaw;
    want.pitch = GAME.pitchDeg * MathUtils.DEG2RAD;
    want.dist = GAME.dist;
    want.fov = mode().fovDeg + 4;
    want.omega = GAME.omega;
    shot = 'game';
  }

  function choose(view: FrameView, realDt: number): void {
    const rp = view.replay;
    if (rp) {
      const rewinding = rp.t < 0;
      if (rp.kind === 'bonkcam') bonkCam(view, rp.focusSlot, rp.killerSlot, rewinding);
      else orbit(view, rp.focusSlot, view.dt, rewinding);
      return;
    }
    if (moment && moment.kind === 'game') return gameOrbit(view, moment, realDt);
    if (moment && moment.kind === 'finalKo') return finalKo(view, moment);
    const spectating = view.localSlot === NO_SLOT && view.focusSlot !== NO_SLOT;
    if (spectating && follow(view, view.focusSlot)) return;
    gameplay(view);
    if (view.roomPhase === RoomPhase.INTRO && !opts.reducedMotion() && !opts.skipIntro) {
      // The sweep starts on the first INTRO frame this client shows (a client that joins late still
      // gets it), squeezed into the INTRO time left; under 0.4 s left it is skipped.
      if (introRound !== view.roundId) {
        introRound = view.roundId;
        const leftSec = -view.roundTick / 60;
        introDur = leftSec >= INTRO.minSec ? Math.min(INTRO.durationTicks / 60, leftSec - 0.15) : 0;
        introAge = 0;
      }
      introAge += realDt;
      const p = introDur > 0 ? MathUtils.clamp(introAge / introDur, 0, 1) : 1;
      if (p < 1) {
        const e = easeOutCubic(p);
        want.pitch = MathUtils.lerp(INTRO.pitchDeg * MathUtils.DEG2RAD, want.pitch, e);
        want.dist = MathUtils.lerp(INTRO.dist, want.dist, e);
        want.yaw = INTRO.yaw * (1 - e);
        shot = 'intro';
      }
    }
  }

  function snapAll(): void {
    tx.snap(want.x);
    ty.snap(want.y);
    th.snap(want.h);
    yaw.snap(want.yaw);
    pitch.snap(want.pitch);
    dist.snap(want.dist);
    fov.snap(want.fov);
  }

  return {
    camera,
    target,
    get trauma() {
      return shake.trauma;
    },
    get shot() {
      return shot;
    },
    update(view) {
      const realDt = lastNow < 0 ? 0 : Math.min(0.1, Math.max(0, (view.now - lastNow) / 1000));
      lastNow = view.now;
      clock += realDt;
      const prevShot = shot;
      const onPodium = view.roomPhase === RoomPhase.PODIUM;
      if (onPodium !== podium) {
        podium = onPodium;
        updateSafe();
      }
      choose(view, realDt);
      const key = (SOURCE_INDEX[view.source] * 8 + view.mapId) * 256 + (view.roundId & 255);
      // Keep yaw continuous: aim for the equivalent angle nearest the current one.
      want.yaw = yaw.x + MathUtils.euclideanModulo(want.yaw - yaw.x + Math.PI, Math.PI * 2) - Math.PI;
      const snap = key !== shotKey || forceCut || shot === 'rewind' || prevShot === 'rewind' || shot === 'intro';
      // The HUD-aware offset applies to the shots the HUD sits over (not replays or the GAME! orbit).
      const hudShot = shot === 'play' || shot === 'follow' || shot === 'intro' || shot === 'finalKo';
      shiftWant = hudShot ? safe.shift : shot === 'menu' && portrait ? MENU_PORTRAIT.shift : 0;
      shiftXWant = hudShot ? safe.shiftX : 0;
      if (snap) {
        shotKey = key;
        forceCut = false;
        snapAll();
        shiftSpring.snap(shiftWant);
        shiftXSpring.snap(shiftXWant);
      } else {
        shiftSpring.step(shiftWant, SPRING.shotOmega, realDt);
        shiftXSpring.step(shiftXWant, SPRING.shotOmega, realDt);
        // Shots ride presentation time (slow motion slows the camera) except the moment shots,
        // which must keep moving while the world crawls.
        // BONK CAM tracks in real time too: at the 0.6× replay rate a presentation-time spring lags a
        // flung victim out of the shot.
        const dt = shot === 'finalKo' || shot === 'game' || shot === 'bonkcam' ? realDt : view.dt;
        const follows = shot === 'play' || shot === 'menu' || shot === 'follow';
        tx.step(want.x, want.omega, dt);
        ty.step(want.y, want.omega, dt);
        th.step(want.h, want.omega, dt);
        yaw.step(want.yaw, SPRING.shotOmega, dt);
        pitch.step(want.pitch, SPRING.shotOmega, dt);
        dist.step(want.dist, follows ? (want.dist > dist.x ? SPRING.distOutOmega : SPRING.distOmega) : SPRING.shotOmega, dt);
        fov.step(want.fov, SPRING.shotOmega, dt);
      }

      const replayShot = shot === 'orbit' || shot === 'bonkcam' || shot === 'rewind' || shot === 'game' || shot === 'finalKo';
      target.set(tx.x, th.x, ty.x);
      const cp = Math.cos(pitch.x);
      camera.position.set(
        target.x + Math.sin(yaw.x) * cp * dist.x,
        target.y + Math.sin(pitch.x) * dist.x,
        target.z + Math.cos(yaw.x) * cp * dist.x,
      );
      const floor = shot === 'bonkcam' ? BONK.floorAbove : replayShot ? TUNE.edge.killZ + VOID_CLEARANCE : MIN_HEIGHT;
      if (camera.position.y < floor) camera.position.y = floor;

      const reduced = opts.reducedMotion();
      shake.update(realDt, reduced ? 0 : opts.shakeScale() * (view.source === 'replay' ? 0.5 : 1));
      camera.up.set(0, 1, 0);
      camera.lookAt(target);
      camera.updateMatrix();
      if (shake.out.x !== 0 || shake.out.y !== 0 || shake.out.roll !== 0) {
        right.setFromMatrixColumn(camera.matrix, 0);
        up.setFromMatrixColumn(camera.matrix, 1);
        camera.position.addScaledVector(right, shake.out.x).addScaledVector(up, shake.out.y);
        camera.rotateZ(shake.out.roll);
      }

      punchLeft = Math.max(0, punchLeft - realDt * 1000);
      let punchK = 0;
      if (!reduced && punchLeft > 0) {
        const f = 1 - punchLeft / punchTotal;
        punchK = f < PUNCH_ATTACK ? f / PUNCH_ATTACK : f < PUNCH_RELEASE ? 1 : 1 - easeOutCubic((f - PUNCH_RELEASE) / (1 - PUNCH_RELEASE));
      }
      const f = fov.x * (1 - punchAmount * punchK);
      applyShift(shiftSpring.x, shiftXSpring.x);
      if (Math.abs(camera.fov - f) > 1e-4) {
        camera.fov = f;
        camera.updateProjectionMatrix();
      }
      camera.updateMatrixWorld();
    },
    addTrauma(a) {
      shake.add(a);
    },
    punch(amount, ms) {
      // A weaker punch never cuts a stronger one short.
      if (punchLeft > 0 && amount < punchAmount) return;
      punchAmount = amount;
      punchLeft = ms;
      punchTotal = Math.max(1, ms);
    },
    resize(w, h, isPortrait) {
      portrait = isPortrait;
      aspect = w / Math.max(1, h);
      viewW = Math.max(1, w);
      viewH = Math.max(1, h);
      appliedShiftPx = Number.NaN;
      updateSafe();
      camera.aspect = aspect;
      applyShift(shiftSpring.x, shiftXSpring.x);
      camera.updateProjectionMatrix();
    },
    setHudInsets(h) {
      hudOverride = h ? { ...h } : null;
      updateSafe();
    },
    get frameShift() {
      return shiftSpring.x;
    },
    setMoment(m) {
      if (m && (!moment || moment.kind !== m.kind)) orbitYaw = yaw.x;
      moment = m;
    },
    cut() {
      forceCut = true;
    },
    holdInFrame(x, y, z, sec) {
      for (let i = holds.length - 1; i >= 0; i--) if (holds[i].until <= clock) holds.splice(i, 1);
      if (holds.length >= 4) holds.shift();
      holds.push({ x, y, z, until: clock + sec });
    },
  };
}
